import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Button,
  Flex,
  HStack,
  IconButton,
  Image,
  useColorMode,
  useDisclosure,
  Stack,
  Text,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  useColorModeValue,
  Link as ChakraLink,
} from '@chakra-ui/react';
import { ChevronDownIcon, HamburgerIcon } from '@chakra-ui/icons';
import { NavLink as RouterNavLink, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { isAuthConfigured } from '../auth/config';
import ThemeSwitcher from './ThemeSwitcher';
import { useProfile } from '../auth/ProfileContext';

import brandLogoLight from '../assets/brand-logo-light-transparent.png';
import brandLogoDark from '../assets/brand-logo-dark-transparent.png';

interface NavItemProps {
  to: string;
  label: string;
  onClick?: () => void;
  color: string;
  highlight?: boolean;
}


const NavItem: React.FC<NavItemProps> = ({ to, label, onClick, color, highlight }) => {
  const highlightColor = useColorModeValue('blue.600', 'blue.300');
  const effectiveColor = highlight ? (color === 'white' ? 'white' : highlightColor) : color;

  return (
    <ChakraLink
      as={RouterNavLink}
      to={to}
      end={to === '/'}
      onClick={onClick}
      color={effectiveColor}
      fontWeight="500"
      whiteSpace="nowrap"
      fontSize={{ base: '1rem', lg: '0.9rem', xl: '0.96rem', '2xl': '1.04rem' }}
      px={{ base: 2, lg: 1, xl: 1.5, '2xl': 2 }}
      py={1}
      rounded="md"
      textShadow={color === 'white' ? '0 2px 8px rgba(0,0,0,0.42)' : 'none'}
      _hover={{
        color: '#FFA500',
        textDecoration: 'none',
      }}
      sx={{
        '&.active': {
          color: '#FFA500',
          textDecoration: 'underline',
          textUnderlineOffset: '4px',
        },
      }}
    >
      {label}
    </ChakraLink>
  );
};

const Header: React.FC = () => {
  const { colorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { pathname } = useLocation();
  const { status, user, signOut } = useAuth();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const [scrolled, setScrolled] = useState(false);

  // The profile row is what the person edits, so it wins over the name baked
  // into the sign-in token at login.
  const displayName = profile?.name || user?.name || user?.email?.split('@')[0] || 'there';
  const firstName = displayName.split(' ')[0];
  const isClient = profile?.role === 'client';

  const handleSignOut = async () => {
    await signOut();
    if (!user?.isFederated) {
      navigate('/login', { replace: true });
    }
  };



  const isHomeTop = pathname === '/' && !scrolled;
  const solidBg = useColorModeValue('gray.100', '#1B202D');
  const bg = isHomeTop ? 'transparent' : solidBg;
  const solidTextColor = useColorModeValue('gray.800', 'white');
  const textColor = isHomeTop ? 'white' : solidTextColor;
  const drawerBg = useColorModeValue('white', '#1B202D');
  const drawerText = useColorModeValue('gray.800', 'white');
  const solidHeaderBorder = useColorModeValue('gray.200', '#2a2f3a');
  const headerBorder = isHomeTop ? 'transparent' : solidHeaderBorder;
  const solidUtilityButtonBg = useColorModeValue('white', 'whiteAlpha.100');
  const utilityButtonBg = isHomeTop ? 'rgba(7,19,33,0.68)' : solidUtilityButtonBg;
  const solidUtilityButtonBorder = useColorModeValue('blackAlpha.200', 'whiteAlpha.200');
  const utilityButtonBorder = isHomeTop ? 'rgba(255,255,255,0.32)' : solidUtilityButtonBorder;
  const logoSrc = isHomeTop || colorMode !== 'light' ? brandLogoLight : brandLogoDark;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box
      as="header"
      bg={bg}
      color={textColor}
      px={{ base: 2, sm: 3, md: 5, xl: 4, '2xl': 6 }}
      py={scrolled ? 2 : { base: 2, sm: 3 }}
      position={pathname === '/' ? 'fixed' : 'sticky'}
      top="0"
      left={0}
      right={0}
      w="100%"
      zIndex="1000"
      borderBottom="1px solid"
      borderColor={headerBorder}
      boxShadow={scrolled ? '0 4px 12px rgba(0,0,0,0.25)' : 'none'}
      backdropFilter={isHomeTop ? 'none' : 'saturate(140%) blur(12px)'}
      transition="all 0.3s ease"
      sx={{ '@media (max-width: 339px)': { paddingInline: '6px' } }}
    >
      <Flex
        flexWrap="nowrap"
        align="center"
        justify="space-between"
        gap={{ base: 1, sm: 2, lg: 3, '2xl': 5 }}
        minW={0}
      >
        <IconButton
          aria-label="Open menu"
          icon={<HamburgerIcon />}
          display={{ base: 'flex', lg: 'none' }}
          onClick={onOpen}
          size="sm"
          minW="32px"
          h="32px"
          flexShrink={0}
          variant="ghost"
          color={textColor}
          bg={utilityButtonBg}
          border="1px solid"
          borderColor={utilityButtonBorder}
          rounded="full"
          _hover={{ bg: utilityButtonBg }}
          sx={{ '@media (max-width: 339px)': { minWidth: '28px', width: '28px', height: '28px' } }}
        />

        {/* Logo */}
        <ChakraLink
          as={RouterLink}
          to="/"
          aria-label="iLand Studio home"
          _hover={{ textDecoration: 'none' }}
          flexShrink={0}
          minW={0}
        >
          <Stack spacing={0} align="center">
            <HStack spacing={{ base: 1, sm: 1.5, md: 2 }} align="center">
              <Text fontSize="sm" color={textColor} display={{ base: 'none', md: 'block' }}>
                This
              </Text>
              <Image
                src={logoSrc}
                alt=""
                h="clamp(24px, 8vw, 34px)"
                w="auto"
                display={{ base: 'none', sm: 'block' }}
              />
              <Text
                fontWeight="bold"
                fontSize={{ base: '18px', sm: 'xl', '2xl': '2xl' }}
                color={textColor}
                whiteSpace="nowrap"
                lineHeight="1"
                sx={{ '@media (max-width: 339px)': { fontSize: '17px' } }}
              >
                <Text as="span" color="#FFA500">i</Text>Land Studio
              </Text>
            </HStack>
            <Text
              color="#FFA500"
              fontSize={{ base: '8px', sm: '9px' }}
              fontWeight="700"
              letterSpacing="0.08em"
              lineHeight="1"
              mt="3px"
              textTransform="uppercase"
            >
              Under development
            </Text>
          </Stack>
        </ChakraLink>

        {/* Desktop nav */}
        <HStack
          spacing={{ lg: 2, xl: 4, '2xl': 5 }}
          display={{ base: 'none', lg: 'flex' }}
          flexShrink={1}
          minW={0}
          justify="center"
          flex="1"
          mx={{ lg: 2, xl: 6 }}
        >
          <NavItem to="/" label="Home" color={textColor} />
          <NavItem to="/platform" label="Studio Platform" color={textColor} highlight />
          <NavItem to="/services" label="Services" color={textColor} />
          <NavItem to="/book" label="Book" color={textColor} />
          <NavItem to="/contact" label="Contact us" color={textColor} />
        </HStack>

        {/* Right side */}
        <Flex align="center" flexWrap="nowrap" gap={{ base: 1, sm: 2, md: 3 }} ml="auto" minW={0}>
          <Box flexShrink={0}>
            <ThemeSwitcher overMedia={isHomeTop} />
          </Box>
          {isAuthConfigured && status === 'signedIn' && (
            <Menu placement="bottom-end">
              <MenuButton
                as={Button}
                size="sm"
                variant="ghost"
                color={textColor}
                minW={0}
                maxW={{ base: '140px', md: '180px' }}
                flexShrink={1}
                h="32px"
                px={{ base: 0, md: 1 }}
                rounded="full"
                _hover={{ bg: utilityButtonBg }}
                _active={{ bg: utilityButtonBg }}
                aria-label={`Account menu for ${displayName}`}
                sx={{ '& > span:first-of-type': { minWidth: 0 } }}
              >
                <HStack spacing={{ base: 1, md: 2 }} minW={0}>
                  <Avatar
                    size="xs"
                    name={displayName}
                    src={profile?.avatarUrl || undefined}
                    bg="orange.400"
                    color="white"
                    boxSize="24px"
                    flexShrink={0}
                  />
                  <Text
                    as="span"
                    fontWeight="600"
                    fontSize={{ base: 'xs', sm: 'sm' }}
                    whiteSpace="nowrap"
                    minW={0}
                    maxW={{ base: 'none', md: '140px' }}
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    Hi {firstName}
                  </Text>
                  <ChevronDownIcon display={{ base: 'none', md: 'inline' }} flexShrink={0} />
                </HStack>
              </MenuButton>
              <MenuList color={drawerText} bg={drawerBg} zIndex={1100} minW="200px">
                <Box px={3} py={2}>
                  <Text fontWeight="700" fontSize="sm" noOfLines={1}>{displayName}</Text>
                  {user?.email && (
                    <Text fontSize="xs" opacity={0.7} noOfLines={1}>{user.email}</Text>
                  )}
                </Box>
                <MenuDivider />
                <MenuItem as={RouterLink} to="/my-galleries" bg={drawerBg}>Shared with me</MenuItem>
                {!isClient && <MenuItem as={RouterLink} to="/galleries" bg={drawerBg}>Client Galleries</MenuItem>}
                {!isClient && <MenuItem as={RouterLink} to="/library" bg={drawerBg}>All photos</MenuItem>}
                <MenuItem as={RouterLink} to="/profile" bg={drawerBg}>My account</MenuItem>
                {profile?.isAdmin && (
                  <MenuItem as={RouterLink} to="/admin" bg={drawerBg}>Platform Admin</MenuItem>
                )}
                <MenuDivider />
                <MenuItem onClick={handleSignOut} bg={drawerBg}>Sign out</MenuItem>
              </MenuList>
            </Menu>
          )}
          {isAuthConfigured && status !== 'signedIn' && (
            <Button
              as={RouterLink}
              to="/login"
              size="sm"
              variant="outline"
              rounded="full"
              color={textColor}
              h="32px"
              flexShrink={0}
              bg={utilityButtonBg}
              borderColor={utilityButtonBorder}
              px={{ base: 2.5, sm: 3 }}
              fontSize={{ base: 'xs', sm: 'sm' }}
              whiteSpace="nowrap"
              _hover={{ bg: utilityButtonBg, color: '#FFA500', textDecoration: 'none' }}
            >
              Sign in
            </Button>
          )}
        </Flex>
      </Flex>

      {/* Mobile drawer */}
      <Drawer placement="left" onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay />
        <DrawerContent bg={drawerBg} color={drawerText}>
          <DrawerCloseButton />
          <DrawerHeader>Menu</DrawerHeader>
          <DrawerBody>
            <Stack spacing={4}>
              <NavItem to="/" label="Home" color={drawerText} onClick={onClose} />
              <NavItem to="/platform" label="Studio Platform" color={drawerText} onClick={onClose} highlight />
              <NavItem to="/services" label="Services" color={drawerText} onClick={onClose} />
              <NavItem to="/book" label="Book" color={drawerText} onClick={onClose} />
              <NavItem to="/contact" label="Contact us" color={drawerText} onClick={onClose} />
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

    </Box>
  );
};

export default Header;
